/**
 * Biography Map Animation Module
 * Handles animated visualization of a person's life journey through locations
 */

class BiographyMapAnimation {
  constructor(map, fragments) {
    this.map = map;
    this.fragments = fragments;
    this.markers = [];
    this.simpleDots = []; // Simple dots shown before playback
    this.currentIndex = 0;
    this.isPlaying = false;
    this.hasCompleted = false;
    this.hasStartedPlayback = false; // Track if playback has ever started
    this.animationSpeed = 2500; // ms between markers (fallback if no audio)
    this.popupDuration = 5500; // ms to show caption (fallback if no audio)
    this.animationTimeout = null;
    this.lineSource = null;
    this.lineLayer = null;
    this.currentAudio = null;
    this.lineSegments = []; // Initialize here to prevent undefined error
    this.finalizeDelayMs = 1500; // Max pause after final audio ends
  }

  /**
   * Initialize the animation system
   */
  init() {
    // Check if mobile
    this.isMobile = window.innerWidth < 768;
    
    // Create line source for movement paths
    this.map.on('load', () => {
      this.setupLineLayer();
    });

    if (this.map.loaded()) {
      this.setupLineLayer();
    }

    // Create all markers but hide them initially with retry logic
    this.initWithRetry();
    
    // Add keyboard navigation
    this.setupKeyboardNavigation();

    // Setup zoom controls
    this.setupZoomControls();

    // Don't auto-show markers on mobile anymore - wait for user interaction
    // This allows mobile users to experience the animation too
  }

  /**
   * Initialize markers and UI with retry logic for DOM readiness
   */
  initWithRetry(retryCount = 0) {
    const maxRetries = 5;
    const retryDelay = 100;
    
    // Check if required DOM elements exist
    const mapContainer = document.querySelector('.biography-map-container');
    const biographyMap = document.getElementById('biographyMap');
    
    if (!mapContainer || !biographyMap) {
      if (retryCount < maxRetries) {
        console.log(`BiographyMapAnimation: DOM not ready, retrying... (${retryCount + 1}/${maxRetries})`);
        setTimeout(() => {
          this.initWithRetry(retryCount + 1);
        }, retryDelay);
      } else {
        console.error('BiographyMapAnimation: Failed to find required DOM elements after retries');
      }
      return;
    }
    
    // DOM is ready, create markers
    this.createMarkers();
  }

  /**
   * Setup the line layer for movement paths
   */
  setupLineLayer() {
    // We'll create line segments dynamically, so no initial setup needed
    this.lineSegments = [];
  }

  /**
   * Setup keyboard navigation for timeline
   */
  setupKeyboardNavigation() {
    document.addEventListener('keydown', (e) => {
      // Only handle arrow keys when map is visible/focused
      const mapContainer = document.querySelector('.biography-map-container');
      if (!mapContainer || !this.map) return;
      
      // Check if UIKit lightbox is open - if so, don't handle navigation
      const lightboxOpen = document.querySelector('.uk-lightbox.uk-open');
      if (lightboxOpen) return;
      
      // Check if the map container or its children have focus, or if no input is focused
      const activeElement = document.activeElement;
      const isInputFocused = activeElement && (
        activeElement.tagName === 'INPUT' || 
        activeElement.tagName === 'TEXTAREA' || 
        activeElement.contentEditable === 'true'
      );
      
      if (isInputFocused) return;
      
      switch(e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          this.navigateToPrevious();
          break;
        case 'ArrowRight':
          e.preventDefault();
          this.navigateToNext();
          break;
        case ' ':
          // Spacebar to play/pause
          e.preventDefault();
          if (this.isPlaying) {
            this.pause();
          } else {
            this.play();
          }
          break;
      }
    });
  }

  /**
   * Navigate to previous timeline item
   */
  navigateToPrevious() {
    if (this.currentIndex <= 0) return;
    
    // Scroll timeline to top of viewport
    this.scrollTimelineToTop();
    
    // Stop any current animation/audio
    if (this.isPlaying) {
      this.pause();
    }
    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio = null;
    }
    
    // Set playing state and update buttons
    this.isPlaying = true;
    const playBtn = document.getElementById('play-btn');
    const pauseBtn = document.getElementById('pause-btn');
    if (playBtn) {
      playBtn.style.visibility = 'hidden';
      playBtn.style.position = 'absolute';
    }
    if (pauseBtn) {
      pauseBtn.style.visibility = 'visible';
      pauseBtn.style.position = 'absolute';
    }
    
    // Move to previous index
    this.currentIndex--;
    this.updateProgressBar();
    
    // Show the fragment
    const fragment = this.fragments[this.currentIndex];
    this.showCaption(fragment);
    this.showMarker(this.currentIndex);
    
    // Start audio if available
    let audioPromise = null;
    if (fragment.audioUrl) {
      audioPromise = this.playAudio(fragment.audioUrl);
    }
    
    // Animate to the marker (going backwards from next location)
    if (this.currentIndex < this.fragments.length - 1) {
      const nextFragment = this.fragments[this.currentIndex + 1];
      // Animate from where we were (next) to where we are now
      this.animateToLocation(
        fragment.lng,
        fragment.lat,
        nextFragment.lng,
        nextFragment.lat
      );
    } else {
      // Last fragment, just animate to it
      this.animateToLocation(fragment.lng, fragment.lat);
    }
    
    // Continue playing after animation if audio exists
    this.map.once('moveend', () => {
      if (audioPromise) {
        audioPromise.then(() => {
          if (this.isPlaying && this.currentIndex < this.fragments.length - 1) {
            this.currentIndex++;
            this.animateNext();
          } else {
            this.pause();
          }
        }).catch(() => {
          setTimeout(() => {
            if (this.isPlaying && this.currentIndex < this.fragments.length - 1) {
              this.currentIndex++;
              this.animateNext();
            } else {
              this.pause();
            }
          }, this.popupDuration);
        });
      } else {
        setTimeout(() => {
          if (this.isPlaying && this.currentIndex < this.fragments.length - 1) {
            this.currentIndex++;
            this.animateNext();
          } else {
            this.pause();
          }
        }, this.popupDuration);
      }
    });
  }

  /**
   * Helper method to scroll timeline to top of viewport
   */
  scrollTimelineToTop() {
    const progressBar = document.getElementById('biography-progress');
    if (progressBar) {
      const rect = progressBar.getBoundingClientRect();
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const targetScroll = rect.top + scrollTop;
      
      window.scrollTo({
        top: targetScroll,
        behavior: 'smooth'
      });
    }
  }

  /**
   * Navigate to next timeline item
   */
  navigateToNext() {
    if (this.currentIndex >= this.fragments.length - 1) return;
    
    // Scroll timeline to top of viewport
    this.scrollTimelineToTop();
    
    // Stop any current animation/audio
    if (this.isPlaying) {
      this.pause();
    }
    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio = null;
    }
    
    // Set playing state and update buttons
    this.isPlaying = true;
    const playBtn = document.getElementById('play-btn');
    const pauseBtn = document.getElementById('pause-btn');
    if (playBtn) {
      playBtn.style.visibility = 'hidden';
      playBtn.style.position = 'absolute';
    }
    if (pauseBtn) {
      pauseBtn.style.visibility = 'visible';
      pauseBtn.style.position = 'absolute';
    }
    
    // Move to next index
    this.currentIndex++;
    this.updateProgressBar();
    
    // Show the fragment
    const fragment = this.fragments[this.currentIndex];
    this.showCaption(fragment);
    this.showMarker(this.currentIndex);
    
    // Start audio if available
    let audioPromise = null;
    if (fragment.audioUrl) {
      audioPromise = this.playAudio(fragment.audioUrl);
    }
    
    // Animate to the marker (going forward from previous location)
    if (this.currentIndex > 0) {
      const prevFragment = this.fragments[this.currentIndex - 1];
      // Animate from where we were to where we are now
      this.animateToLocation(
        fragment.lng,
        fragment.lat,
        prevFragment.lng,
        prevFragment.lat
      );
    } else {
      // First fragment, just animate to it
      this.animateToLocation(fragment.lng, fragment.lat);
    }
    
    // Continue playing after animation if audio exists
    this.map.once('moveend', () => {
      if (audioPromise) {
        audioPromise.then(() => {
          if (this.isPlaying && this.currentIndex < this.fragments.length - 1) {
            this.currentIndex++;
            this.animateNext();
          } else {
            this.pause();
          }
        }).catch(() => {
          setTimeout(() => {
            if (this.isPlaying && this.currentIndex < this.fragments.length - 1) {
              this.currentIndex++;
              this.animateNext();
            } else {
              this.pause();
            }
          }, this.popupDuration);
        });
      } else {
        setTimeout(() => {
          if (this.isPlaying && this.currentIndex < this.fragments.length - 1) {
            this.currentIndex++;
            this.animateNext();
          } else {
            this.pause();
          }
        }, this.popupDuration);
      }
    });
  }

  /**
   * Create all markers (hidden initially)
   */
  createMarkers() {
    // First, create simple dots for initial view
    this.createSimpleDots();

    // Create markers array with same length as fragments, allowing nulls
    this.markers = new Array(this.fragments.length);

    // Determine language from document or default to 'en'
    const isGerman = document.documentElement.lang === 'de' ||
                     document.querySelector('html[lang="de"]') !== null;

    this.fragments.forEach((fragment) => {
      const index = this.fragments.indexOf(fragment);
      // Validate coordinates
      if (!fragment.lng || !fragment.lat || isNaN(fragment.lng) || isNaN(fragment.lat)) {
        console.warn(`Invalid coordinates for fragment ${index}:`, fragment);
        this.markers[index] = null; // Keep null at this index
        return;
      }

      // Create marker element with age label
      const el = document.createElement('div');
      el.className = 'biography-marker-wrapper';
      el.dataset.index = index;
      el.style.display = 'none'; // Hide full markers initially
      
      // Add age label if available (handle 0 as valid)
      let ageText = '';
      if (fragment.age !== null && fragment.age !== undefined) {
        ageText = isGerman ? `ALTER ${fragment.age}` : `AGE ${fragment.age}`;
      }
      
      el.innerHTML = `
        <div class="biography-marker"></div>
        ${ageText ? `<div class="biography-marker-label">${ageText}</div>` : ''}
      `;

      const marker = new mapboxgl.Marker({
        element: el,
        anchor: 'center',
        draggable: false
      });
      
      // Set position and add to map
      const lng = parseFloat(fragment.lng);
      const lat = parseFloat(fragment.lat);
      // Marker created at index
      marker.setLngLat([lng, lat]);
      marker.addTo(this.map);

      // Add click handler to show caption and play audio
      el.addEventListener('click', () => {
        this.onMarkerClick(index);
      });

      // Store marker reference at correct index
      this.markers[index] = marker;
    });
    
    // Create caption container
    this.createCaptionContainer();
  }

  /**
   * Create caption container for movie-style subtitles
   */
  createCaptionContainer() {
    const mapContainer = document.querySelector('.biography-map-container');
    const biographyMap = document.getElementById('biographyMap');
    
    // Check if required elements exist
    if (!mapContainer || !biographyMap) {
      console.error('BiographyMapAnimation: Required DOM elements not found', {
        mapContainer: !!mapContainer,
        biographyMap: !!biographyMap
      });
      return;
    }
    
    // Check if mobile - skip progress bar creation entirely
    const isMobile = window.innerWidth < 768;
    if (isMobile) {
      // Only create caption container on mobile
      const container = document.createElement('div');
      container.id = 'biography-caption';
      container.className = 'biography-caption';
      container.style.display = 'none';
      mapContainer.appendChild(container);
      return; // Exit early - no progress bar on mobile
    }
    
    // Check if progress bar already exists (avoid duplicates)
    if (document.getElementById('biography-progress')) {
      console.warn('BiographyMapAnimation: Progress bar already exists, skipping creation');
      return;
    }
    
    // Create progress bar BEFORE map (timeline at top) - DESKTOP ONLY
    const progressBar = document.createElement('div');
    progressBar.id = 'biography-progress';
    progressBar.className = 'biography-progress';
    mapContainer.insertBefore(progressBar, biographyMap);
    
    // Create caption container after map (red banner at bottom)
    const container = document.createElement('div');
    container.id = 'biography-caption';
    container.className = 'biography-caption';
    container.style.display = 'none';
    mapContainer.appendChild(container);
    
    // Initialize progress bar
    this.initializeProgressBar();
    // Set initial position and update
    setTimeout(() => {
      this.updateProgressBar();

      // Ensure progress bar is visible
      const progressBar = document.getElementById('biography-progress');
      if (progressBar) {
        progressBar.style.display = 'block';
        progressBar.style.visibility = 'visible';
      }

      // Initialize scroll management
      this.initializeScrollManagement();

      // Don't interfere with scrolling - let browser handle it naturally
    }, 200); // Longer delay to ensure layout is complete
  }
  
  /**
   * Initialize progress bar with circles for each age
   */
  initializeProgressBar() {
    const progressBar = document.getElementById('biography-progress');
    if (!progressBar) {
      console.error('BiographyMapAnimation: Progress bar element not found');
      return;
    }

    // Hide timeline if there's only 1 item or less
    if (this.fragments.length <= 1) {
      progressBar.style.display = 'none';
      progressBar.style.visibility = 'hidden';
      console.log('InitializeProgressBar: Only 1 or fewer items, hiding timeline');
      return;
    }

    // Ensure visibility
    progressBar.style.display = 'block';
    progressBar.style.visibility = 'visible';

    console.log('InitializeProgressBar: Progress bar found and made visible');
    
    const isMobile = window.innerWidth < 768;
    
    let progressHtml = `
      <div class="progress-track-wrapper">
        <span class="age-label">AGE</span>
        <div class="progress-track">`;
    
    this.fragments.forEach((fragment, index) => {
      // Handle age 0 as valid, only use nbsp for null/undefined
      const ageText = (fragment.age !== null && fragment.age !== undefined) ? fragment.age : '&nbsp;';
      progressHtml += `
        <div class="progress-item ${index === 0 ? 'active' : ''}" data-index="${index}">
          <div class="progress-circle"></div>
          <div class="progress-label">${ageText}</div>
        </div>
      `;
    });
    
    progressHtml += `</div>
        <div class="progress-gradient-left"></div>
        <div class="progress-gradient-right"></div>
      </div>
    `;
    
    progressBar.innerHTML = progressHtml;
    
    // Add click/touch handlers to progress items
    progressBar.querySelectorAll('.progress-item').forEach((item, index) => {
      // Prevent text selection on mobile
      item.style.userSelect = 'none';
      item.style.webkitUserSelect = 'none';
      
      const handleItemSelect = (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        // Scroll timeline to top of viewport when clicking an age
        this.scrollTimelineToTop();
        
        // Stop any current animation/audio
        if (this.isPlaying) {
          this.pause();
        }
        if (this.currentAudio) {
          this.currentAudio.pause();
          this.currentAudio = null;
        }
        
        // Set playing state
        this.isPlaying = true;

        // Update play/pause button states immediately (match display-based toggling)
        const playBtn = document.getElementById('play-btn');
        const pauseBtn = document.getElementById('pause-btn');
        if (playBtn) {
          playBtn.style.display = 'none';
        }
        if (pauseBtn) {
          pauseBtn.style.display = 'flex';
        }
        
        // Set the current index
        this.currentIndex = index;
        // Immediately update visual state for responsiveness
        this.updateProgressBar();
        
        // Show caption immediately
        const fragment = this.fragments[index];
        this.showCaption(fragment);
        
        // Show the marker
        this.showMarker(index);
        
        // Start audio immediately if available
        let audioPromise = null;
        if (fragment.audioUrl) {
          audioPromise = this.playAudio(fragment.audioUrl);
        }
        
        // Animate to the marker position using unified animation
        this.animateToLocation(fragment.lng, fragment.lat);
        
        // After map animation completes, add delay for dial to settle, then continue
        this.map.once('moveend', () => {
          // Add 300ms delay to let the dial settle before continuing
          setTimeout(() => {
            // Set up continuation based on audio or timer
            if (audioPromise) {
              // Wait for audio to complete then continue
              audioPromise.then(() => {
                if (this.isPlaying && this.currentIndex < this.fragments.length - 1) {
                  this.currentIndex++;
                  this.animateNext();
                }
              }).catch(() => {
                // Audio failed, use timer
                setTimeout(() => {
                  if (this.isPlaying && this.currentIndex < this.fragments.length - 1) {
                    this.currentIndex++;
                    this.animateNext();
                  }
                }, this.popupDuration);
              });
            } else {
              // Use timer if no audio
              setTimeout(() => {
                if (this.isPlaying && this.currentIndex < this.fragments.length - 1) {
                  this.currentIndex++;
                  this.animateNext();
                }
              }, this.popupDuration);
            }
          }, 300); // Delay to let dial settle
        });
      };
      
      // Add both click and touch support
      item.addEventListener('click', handleItemSelect);
      item.addEventListener('touchend', handleItemSelect);
    });
    
    // Add swipe gesture support for mobile
    if (isMobile) {
      const wrapper = progressBar.querySelector('.progress-track-wrapper');
      if (wrapper) {
        let scrollTimeout;
        
        // Detect when scrolling stops and potentially snap to nearest item
        wrapper.addEventListener('scroll', () => {
          clearTimeout(scrollTimeout);
          
          scrollTimeout = setTimeout(() => {
            // Optional: Add snap-to-nearest-item logic here if needed
          }, 150);
        });
        
        // Add visual feedback when scrolling
        wrapper.addEventListener('touchstart', () => {
          wrapper.style.scrollBehavior = 'auto'; // Disable smooth scroll during touch
        });
        
        wrapper.addEventListener('touchend', () => {
          setTimeout(() => {
            wrapper.style.scrollBehavior = 'smooth'; // Re-enable smooth scroll
          }, 100);
        });
      }
    }
    
    // Navigation arrows removed - Age label added instead
  }
  
  /**
   * Update progress bar to show current position
   */
  updateProgressBar() {
    // Skip on mobile - no progress bar to update
    if (window.innerWidth < 768) {
      return;
    }

    const progressItems = document.querySelectorAll('.progress-item');

    progressItems.forEach((item, index) => {
      item.classList.toggle('active', index === this.currentIndex);
      item.classList.toggle('completed', index < this.currentIndex);
    });

    // Navigation arrows removed

    // Auto-scroll to keep current item in view during playback
    if (this.isPlaying) {
      this.scrollToCurrentItem();
    }
  }

  /**
   * Play the animation
   */
  play() {
    if (this.isPlaying) return;

    // If animation is complete, restart it from the beginning
    if (this.currentIndex >= this.fragments.length - 1) {
      // Reset to beginning
      this.currentIndex = 0;
      this.hasCompleted = false;

      // Reset all markers to hidden state
      this.markers.forEach(marker => {
        if (marker) {
          const el = marker.getElement();
          el.style.display = 'none';
          el.classList.remove('active', 'passed-1', 'passed-2', 'passed-3', 'passed-4', 'passed-far');
        }
      });

      // Reset to simple dots
      this.hasStartedPlayback = false;
      this.simpleDots.forEach(dot => {
        if (dot) {
          dot.getElement().style.display = 'block';
        }
      });

      // Clear all line segments
      this.lineSegments.forEach(segmentId => {
        if (this.map.getLayer(segmentId)) {
          this.map.removeLayer(segmentId);
        }
        if (this.map.getSource(segmentId)) {
          this.map.removeSource(segmentId);
        }
      });
      this.lineSegments = [];

      // Hide caption
      this.hideCaption();

      // Update progress bar
      this.updateProgressBar();
    }

    this.isPlaying = true;

    // Update play/pause button states IMMEDIATELY for instant feedback
    const playBtn = document.getElementById('play-btn');
    const pauseBtn = document.getElementById('pause-btn');
    if (playBtn) {
      playBtn.style.display = 'none';
    }
    if (pauseBtn) {
      pauseBtn.style.display = 'flex';
    }

    // If this is the first playback, hide simple dots and show full markers
    if (!this.hasStartedPlayback) {
      this.hasStartedPlayback = true;
      this.switchToFullMarkers();
    }

    this.animateNext();
  }

  /**
   * Pause the animation
   */
  pause() {
    this.isPlaying = false;
    if (this.animationTimeout) {
      clearTimeout(this.animationTimeout);
    }

    // Update play/pause button states immediately
    const playBtn = document.getElementById('play-btn');
    const pauseBtn = document.getElementById('pause-btn');
    if (playBtn) {
      playBtn.style.display = 'flex';
    }
    if (pauseBtn) {
      pauseBtn.style.display = 'none';
    }

    // Stop any map animations immediately
    if (this.map) {
      this.map.stop();
    }

    // Stop any playing audio
    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio = null;
    }

    // Zoom out to fit all markers when paused
    this.fitAllMarkers();
  }

  /**
   * Restart the animation
   */
  restart() {
    this.pause();
    this.currentIndex = 0;
    
    // Reset all markers to hidden state
    this.markers.forEach(marker => {
      if (marker) {
        const el = marker.getElement();
        el.style.display = 'none';
        el.classList.remove('active', 'passed-1', 'passed-2', 'passed-3', 'passed-4', 'passed-far');
      }
    });

    // Reset to simple dots
    this.hasStartedPlayback = false;
    this.simpleDots.forEach(dot => {
      if (dot) {
        dot.getElement().style.display = 'block';
      }
    });

    // Clear all line segments
    this.lineSegments.forEach(segmentId => {
      if (this.map.getLayer(segmentId)) {
        this.map.removeLayer(segmentId);
      }
      if (this.map.getSource(segmentId)) {
        this.map.removeSource(segmentId);
      }
    });
    this.lineSegments = [];

    // Hide caption
    this.hideCaption();

    // Start again
    setTimeout(() => this.play(), 500);
  }


  /**
   * Calculate distance between two coordinates
   */
  calculateDistance(lng1, lat1, lng2, lat2) {
    const R = 6371; // Earth's radius in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLng/2) * Math.sin(dLng/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  }

  /**
   * Unified animation function with distance-based arc
   * Creates smooth, cinematic camera movements that show geographic context
   */
  animateToLocation(destLng, destLat, sourceLng = null, sourceLat = null) {
    // If no source provided, use current center
    if (sourceLng === null || sourceLat === null) {
      const center = this.map.getCenter();
      sourceLng = center.lng;
      sourceLat = center.lat;
    }
    
    // Calculate distance
    const distance = this.calculateDistance(sourceLng, sourceLat, destLng, destLat);
    
    // Determine animation parameters based on distance
    let minZoom, duration, curve;
    
    if (distance < 50) {
      // Very short distance - subtle arc
      minZoom = 12;
      duration = 3500;
      curve = 1.2;
    } else if (distance < 200) {
      // Short distance - moderate arc
      minZoom = 10;
      duration = 4000;
      curve = 1.4;
    } else if (distance < 500) {
      // Medium distance - clear arc
      minZoom = 8;
      duration = 4500;
      curve = 1.5;
    } else if (distance < 1500) {
      // Long distance - high arc
      minZoom = 6;
      duration = 5000;
      curve = 1.6;
    } else if (distance < 5000) {
      // Very long distance - very high arc
      minZoom = 4;
      duration = 5500;
      curve = 1.8;
    } else {
      // Extreme distance - maximum arc
      minZoom = 2;
      duration = 6000;
      curve = 2.0;
    }
    
    // Log for debugging
    console.log(`Animating ${distance.toFixed(0)}km: minZoom=${minZoom}, duration=${duration}ms, curve=${curve}`);
    
    // Perform the animation
    return this.map.flyTo({
      center: [destLng, destLat],
      zoom: 14, // Final zoom level
      minZoom: minZoom, // Creates the arc effect
      duration: duration,
      curve: curve,
      easing: (t) => {
        // Custom easing for more natural movement
        // Ease in-out cubic
        return t < 0.5 
          ? 4 * t * t * t 
          : 1 - Math.pow(-2 * t + 2, 3) / 2;
      },
      essential: true
    });
  }

  /**
   * Animate the next marker in sequence
   */
  animateNext() {
    if (!this.isPlaying || this.currentIndex >= this.fragments.length) {
      this.onComplete();
      return;
    }

    const fragment = this.fragments[this.currentIndex];
    const marker = this.markers[this.currentIndex];

    // Skip if no marker (invalid coordinates)
    if (!marker) {
      console.warn(`Skipping fragment ${this.currentIndex} - no valid marker`);
      this.currentIndex++;
      setTimeout(() => this.animateNext(), 100);
      return;
    }

    // Remove pulse from previous marker
    if (this.currentIndex > 0) {
      const prevMarker = this.markers[this.currentIndex - 1];
      if (prevMarker) {
        prevMarker.getElement().classList.remove('active');
      }
    }

    // Show marker and add pulse
    const el = marker.getElement();
    el.style.display = 'flex'; // Use flex for wrapper
    el.classList.add('active');
    
    // Update opacity for previous markers (show last 4 with decreasing opacity)
    this.markers.forEach((marker, i) => {
      if (!marker) return;
      const el = marker.getElement();

      if (i < this.currentIndex) {
        el.style.display = 'flex';
        const distance = this.currentIndex - i;
        el.classList.remove('passed-1', 'passed-2', 'passed-3', 'passed-4', 'passed-far');

        if (distance === 1) {
          el.classList.add('passed-1'); // 50% opacity
        } else if (distance === 2) {
          el.classList.add('passed-2'); // 25% opacity
        } else if (distance === 3) {
          el.classList.add('passed-3'); // 10% opacity
        } else if (distance >= 4) {
          el.classList.add('passed-far'); // 0% opacity
        }
      } else if (i === this.currentIndex) {
        // Current marker should be visible
        el.style.display = 'flex';
      } else if (i > this.currentIndex) {
        // Hide future markers during animation
        el.style.display = 'none';
        el.classList.remove('passed-1', 'passed-2', 'passed-3', 'passed-4', 'passed-far');
      }
    });

    // Update line
    this.updateLine();

    // Get previous fragment for animation
    let prevFragment = null;
    if (this.currentIndex > 0) {
      prevFragment = this.fragments[this.currentIndex - 1];
    }

    // Show caption and start audio immediately
    this.showCaption(fragment);
    
    let audioPromise = null;
    if (fragment.audioUrl) {
      audioPromise = this.playAudio(fragment.audioUrl);
    }
    
    // Use unified animation function
    if (this.currentIndex === 0) {
      // First marker - animate from current view
      this.animateToLocation(fragment.lng, fragment.lat);
    } else {
      // Animate from previous marker to current
      this.animateToLocation(
        fragment.lng, 
        fragment.lat, 
        prevFragment.lng, 
        prevFragment.lat
      );
    }
    
    // Handle completion after animation
    this.map.once('moveend', () => {
      if (!this.isPlaying) return;
      this.handleAudioCompletion(audioPromise);
    });
  }
  
  /**
   * Handle audio completion and proceed to next
   */
  handleAudioCompletion(audioPromise) {
    if (audioPromise) {
      audioPromise.then(() => {
        if (!this.isPlaying) return;
        // If we're on the last fragment when audio ends, complete immediately
        if (this.currentIndex >= this.fragments.length - 1) {
          this.finalizeSoon();
        } else {
          this.proceedToNext();
        }
      }).catch(() => {
        // Audio failed, fall back to 4-second timer
        this.animationTimeout = setTimeout(() => {
          if (this.currentIndex >= this.fragments.length - 1) {
            this.finalizeSoon();
          } else {
            this.proceedToNext();
          }
        }, this.popupDuration); // Use popupDuration (4 seconds)
      });
    } else {
      // No audio, use 4-second timer minimum
      this.animationTimeout = setTimeout(() => {
        if (this.currentIndex >= this.fragments.length - 1) {
          this.finalizeSoon();
        } else {
          this.proceedToNext();
        }
      }, this.popupDuration); // Use popupDuration (4 seconds)
    }
  }

  /**
   * Play audio and return a promise that resolves when audio ends
   */
  playAudio(audioUrl) {
    return new Promise((resolve, reject) => {
      // Stop any currently playing audio
      if (this.currentAudio) {
        this.currentAudio.pause();
        this.currentAudio = null;
      }
      
      const audio = new Audio(audioUrl);
      this.currentAudio = audio;
      
      audio.addEventListener('ended', () => {
        this.currentAudio = null;
        // If this was the last fragment, immediately complete and zoom out
        if (this.currentIndex >= this.fragments.length - 1 && !this.hasCompleted) {
          this.finalizeSoon();
        }
        resolve();
      });
      
      audio.addEventListener('error', (e) => {
        console.warn('Audio playback failed:', e);
        this.currentAudio = null;
        reject(e);
      });
      
      // Start playing
      audio.play().catch((e) => {
        console.warn('Audio play failed:', e);
        this.currentAudio = null;
        reject(e);
      });
    });
  }

  /**
   * Schedule completion shortly after audio ends (bounded pause)
   */
  finalizeSoon() {
    if (this.hasCompleted) return;
    if (this.animationTimeout) clearTimeout(this.animationTimeout);
    // Ensure we start the zoom within a small, consistent delay
    setTimeout(() => {
      if (!this.hasCompleted) this.onComplete();
    }, this.finalizeDelayMs);
  }
  
  /**
   * Proceed to next marker
   */
  proceedToNext() {
    this.hideCaption();
    this.currentIndex++;
    
    // Small pause before next marker
    setTimeout(() => {
      this.animateNext();
    }, 500);
  }

  /**
   * Go to next marker manually
   */
  goToNext() {
    if (this.currentIndex >= this.fragments.length - 1) return;
    
    // Stop current audio if playing
    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio = null;
    }
    
    // Don't stop if playing - just move to next
    if (!this.isPlaying) {
      this.hideCaption();
    }
    
    // Move to next marker
    this.currentIndex++;
    this.showMarker(this.currentIndex);
    
    // Play audio for new fragment
    const fragment = this.fragments[this.currentIndex];
    if (fragment && fragment.audioUrl) {
      this.playAudio(fragment.audioUrl).catch(() => {
        console.log('Audio playback failed for next');
      });
    }
    
    // If playing, continue animation from new position
    if (this.isPlaying) {
      clearTimeout(this.animationTimeout);
      this.animationTimeout = setTimeout(() => {
        this.proceedToNext();
      }, this.popupDuration);
    }
  }

  /**
   * Go to previous marker manually
   */
  goToPrevious() {
    if (this.currentIndex <= 0) return;
    
    // Stop current audio if playing
    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio = null;
    }
    
    // Don't stop if playing - just move to previous
    if (!this.isPlaying) {
      this.hideCaption();
    }
    
    // Move to previous marker
    this.currentIndex--;
    this.showMarker(this.currentIndex);
    
    // Play audio for new fragment
    const fragment = this.fragments[this.currentIndex];
    if (fragment && fragment.audioUrl) {
      this.playAudio(fragment.audioUrl).catch(() => {
        console.log('Audio playback failed for previous');
      });
    }
    
    // If playing, continue animation from new position
    if (this.isPlaying) {
      clearTimeout(this.animationTimeout);
      this.animationTimeout = setTimeout(() => {
        this.proceedToNext();
      }, this.popupDuration);
    }
  }

  /**
   * Show a specific marker and its caption
   */
  showMarker(index) {
    if (index < 0 || index >= this.fragments.length) return;
    
    const fragment = this.fragments[index];
    const marker = this.markers[index];
    
    if (!marker) return;
    
    // Update all markers visibility and state
    this.markers.forEach((m, i) => {
      if (!m) return;
      const el = m.getElement();
      
      // Remove all state classes first
      el.classList.remove('active', 'passed-1', 'passed-2', 'passed-3', 'passed-4', 'passed-far');

      if (i === index) {
        // Current marker
        el.style.display = 'flex';
        el.classList.add('active');
      } else if (i < index) {
        // Past markers
        el.style.display = 'flex';
        const distance = index - i;

        if (distance === 1) {
          el.classList.add('passed-1'); // 50% opacity
        } else if (distance === 2) {
          el.classList.add('passed-2'); // 25% opacity
        } else if (distance === 3) {
          el.classList.add('passed-3'); // 10% opacity
        } else if (distance >= 4) {
          el.classList.add('passed-far'); // 0% opacity
        }
      } else {
        // Future markers - hide them
        el.style.display = 'none';
      }
    });
    
    // Update line to current position
    this.updateLineToIndex(index);
    
    // Show caption immediately for responsiveness
    this.showCaption(fragment);
    
    // Animate to the marker using unified animation
    this.animateToLocation(fragment.lng, fragment.lat);
  }

  /**
   * Update line segments up to a specific index
   */
  updateLineToIndex(targetIndex) {
    // Clear existing line segments
    this.lineSegments.forEach(segmentId => {
      if (this.map.getLayer(segmentId)) {
        this.map.removeLayer(segmentId);
      }
      if (this.map.getSource(segmentId)) {
        this.map.removeSource(segmentId);
      }
    });
    this.lineSegments = [];
    
    // Add line segments up to target index
    for (let i = 1; i <= targetIndex && i < this.fragments.length; i++) {
      const segmentId = `journey-line-${i-1}-${i}`;
      const coordinates = [
        [this.fragments[i-1].lng, this.fragments[i-1].lat],
        [this.fragments[i].lng, this.fragments[i].lat]
      ];
      
      this.map.addSource(segmentId, {
        type: 'geojson',
        data: {
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'LineString',
            coordinates: coordinates
          }
        }
      });
      
      const age = targetIndex - i;
      const opacity = Math.max(0.2, 1.0 - (age * 0.15));
      
      this.map.addLayer({
        id: segmentId,
        type: 'line',
        source: segmentId,
        layout: {
          'line-join': 'round',
          'line-cap': 'round'
        },
        paint: {
          'line-color': '#888',
          'line-width': 2,
          'line-dasharray': [2, 2],
          'line-opacity': opacity
        }
      });
      
      this.lineSegments.push(segmentId);
    }
  }

  /**
   * Show caption with quote and optional image
   */
  showCaption(fragment) {
    const caption = document.getElementById('biography-caption');
    
    // Use raw text - CSS text-wrap: balance will handle line distribution
    caption.innerHTML = `
      <div class="caption-content">
        <div class="caption-text">
          <div class="caption-quote">${fragment.quote}</div>
        </div>
      </div>
    `;
    
    // Update progress bar
    this.updateProgressBar();
    
    // Ensure caption is always visible
    if (caption.style.display !== 'flex') {
      caption.style.display = 'flex';
      caption.classList.add('visible');
    }
  }

  /**
   * Hide caption
   */
  hideCaption() {
    // Don't hide caption at all - keep it always visible
    // Just let content update happen
  }

  /**
   * Update the journey line with decreasing opacity for older segments
   */
  updateLine() {
    // Only add a new line segment if we have at least 2 points
    if (this.currentIndex === 0) return;
    
    const fromIndex = this.currentIndex - 1;
    const toIndex = this.currentIndex;
    
    // Create new line segment
    const segmentId = `journey-line-${fromIndex}-${toIndex}`;
    const coordinates = [
      [this.fragments[fromIndex].lng, this.fragments[fromIndex].lat],
      [this.fragments[toIndex].lng, this.fragments[toIndex].lat]
    ];
    
    // Check if source already exists, remove if it does
    if (this.map.getSource(segmentId)) {
      if (this.map.getLayer(segmentId)) {
        this.map.removeLayer(segmentId);
      }
      this.map.removeSource(segmentId);
    }
    
    // Add source for this segment
    this.map.addSource(segmentId, {
      type: 'geojson',
      data: {
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'LineString',
          coordinates: coordinates
        }
      }
    });
    
    // Add layer for this segment with full opacity
    this.map.addLayer({
      id: segmentId,
      type: 'line',
      source: segmentId,
      layout: {
        'line-join': 'round',
        'line-cap': 'round'
      },
      paint: {
        'line-color': '#888',
        'line-width': 2,
        'line-dasharray': [2, 2],
        'line-opacity': 1.0
      }
    });
    
    // Track this segment
    this.lineSegments.push(segmentId);
    
    // Update opacity of all existing segments
    this.updateSegmentOpacities();
  }
  
  /**
   * Update opacity of all line segments - newest is 100%, older ones fade
   */
  updateSegmentOpacities() {
    this.lineSegments.forEach((segmentId, index) => {
      // Calculate opacity: newest segment is 1.0, older ones decrease
      const age = this.lineSegments.length - 1 - index;
      const opacity = Math.max(0.2, 1.0 - (age * 0.15)); // Fade by 15% per segment, minimum 20%
      
      if (this.map.getLayer(segmentId)) {
        this.map.setPaintProperty(segmentId, 'line-opacity', opacity);
      }
    });
  }

  /**
   * Create simple red dots for initial view
   */
  createSimpleDots() {
    this.fragments.forEach((fragment, index) => {
      // Validate coordinates
      if (!fragment.lng || !fragment.lat || isNaN(fragment.lng) || isNaN(fragment.lat)) {
        return;
      }

      // Create simple dot element
      const el = document.createElement('div');
      el.className = 'biography-simple-dot';
      el.dataset.index = index;

      const marker = new mapboxgl.Marker({
        element: el,
        anchor: 'center',
        draggable: false
      });

      // Set position and add to map
      const lng = parseFloat(fragment.lng);
      const lat = parseFloat(fragment.lat);
      marker.setLngLat([lng, lat]);
      marker.addTo(this.map);

      // Store reference
      this.simpleDots.push(marker);
    });
  }

  /**
   * Switch from simple dots to full markers
   */
  switchToFullMarkers() {
    // Hide all simple dots
    this.simpleDots.forEach(dot => {
      if (dot) {
        dot.getElement().style.display = 'none';
      }
    });

    // Show markers progressively as animation proceeds
    // (they'll be shown by the animation logic)
  }

  /**
   * Fit all markers in view
   */
  fitAllMarkers() {
    if (!this.markers || this.markers.length === 0) return;

    // Calculate bounds to include all markers
    const bounds = new mapboxgl.LngLatBounds();

    this.fragments.forEach((fragment) => {
      if (fragment.lng && fragment.lat && !isNaN(fragment.lng) && !isNaN(fragment.lat)) {
        bounds.extend([fragment.lng, fragment.lat]);
      }
    });

    // Fit map to bounds with padding
    this.map.fitBounds(bounds, {
      padding: { top: 50, bottom: 50, left: 50, right: 50 },
      duration: 1500,
      maxZoom: 12 // Don't zoom in too much
    });
  }

  /**
   * Handle marker click - show caption and play audio
   */
  onMarkerClick(index) {
    // Don't respond to clicks during animation
    if (this.isPlaying) return;

    const fragment = this.fragments[index];
    if (!fragment) return;

    // Update the timeline to match the clicked marker
    this.currentIndex = index;
    this.updateProgressBar();

    // Show the marker at this position
    this.showMarker(index);

    // Play audio if available
    if (fragment.audioUrl) {
      this.playAudio(fragment.audioUrl).catch(() => {
        console.log('Audio playback failed for clicked marker');
      });
    }
  }

  /**
   * Initialize scroll management for timeline
   */
  initializeScrollManagement() {
    const wrapper = document.querySelector('.progress-track-wrapper');
    if (!wrapper) return;

    // Always start at the beginning (first year)
    wrapper.scrollLeft = 0;

    // Initial gradient check
    this.updateGradientOverlays();

    // Listen for scroll events to update gradients
    wrapper.addEventListener('scroll', () => {
      this.updateGradientOverlays();
    });

    // Listen for resize events to update gradients
    window.addEventListener('resize', () => {
      setTimeout(() => {
        this.updateGradientOverlays();
      }, 100);
    });
  }

  /**
   * Update gradient overlays based on scroll position and overflow
   */
  updateGradientOverlays() {
    const wrapper = document.querySelector('.progress-track-wrapper');
    const track = document.querySelector('.progress-track');
    const gradientLeft = document.querySelector('.progress-gradient-left');
    const gradientRight = document.querySelector('.progress-gradient-right');

    if (!wrapper || !track || !gradientLeft || !gradientRight) return;

    const wrapperWidth = wrapper.clientWidth;
    const trackWidth = track.scrollWidth;
    const scrollLeft = wrapper.scrollLeft;
    const maxScroll = trackWidth - wrapperWidth;

    // Only show gradients if there's overflow
    const hasOverflow = trackWidth > wrapperWidth;

    // Update justify-content based on overflow
    if (hasOverflow) {
      wrapper.style.justifyContent = 'flex-start';
    } else {
      wrapper.style.justifyContent = 'center';
    }

    if (!hasOverflow) {
      gradientLeft.classList.remove('visible');
      gradientRight.classList.remove('visible');
      return;
    }

    // Show left gradient if scrolled right (can scroll back left)
    if (scrollLeft > 10) {
      gradientLeft.classList.add('visible');
    } else {
      gradientLeft.classList.remove('visible');
    }

    // Show right gradient if can scroll more right
    if (scrollLeft < maxScroll - 10) {
      gradientRight.classList.add('visible');
    } else {
      gradientRight.classList.remove('visible');
    }
  }

  /**
   * Scroll to keep current progress item in view during playback
   */
  scrollToCurrentItem() {
    const wrapper = document.querySelector('.progress-track-wrapper');
    const activeItem = document.querySelector('.progress-item.active');

    if (!wrapper || !activeItem) return;

    const wrapperRect = wrapper.getBoundingClientRect();
    const itemRect = activeItem.getBoundingClientRect();

    // Calculate item position relative to wrapper
    const itemLeft = activeItem.offsetLeft;
    const itemWidth = activeItem.offsetWidth;
    const wrapperWidth = wrapper.clientWidth;
    const currentScroll = wrapper.scrollLeft;

    // Calculate ideal scroll position to center the item
    const idealScroll = itemLeft - (wrapperWidth / 2) + (itemWidth / 2);

    // Only scroll if the item is not fully visible
    const itemVisibleLeft = itemLeft - currentScroll;
    const itemVisibleRight = itemVisibleLeft + itemWidth;

    const margin = 60; // Buffer margin

    if (itemVisibleLeft < margin || itemVisibleRight > wrapperWidth - margin) {
      wrapper.scrollTo({
        left: Math.max(0, idealScroll),
        behavior: 'smooth'
      });

      // Update gradients after scroll
      setTimeout(() => {
        this.updateGradientOverlays();
      }, 300);
    }
  }

  /**
   * Show all markers at once (for mobile)
   */
  showAllMarkers() {
    // Show all markers without duplicate active states
    this.markers.forEach((marker) => {
      if (marker) {
        const el = marker.getElement();
        el.style.display = 'flex'; // Use flex for wrapper
        // Remove any active state to prevent duplicates
        el.classList.remove('active', 'passed-1', 'passed-2', 'passed-3', 'passed-4', 'passed-far');
      }
    });

    // Draw all line segments at once for mobile
    for (let i = 1; i < this.fragments.length; i++) {
      const segmentId = `journey-line-${i-1}-${i}`;
      const coordinates = [
        [this.fragments[i-1].lng, this.fragments[i-1].lat],
        [this.fragments[i].lng, this.fragments[i].lat]
      ];
      
      this.map.addSource(segmentId, {
        type: 'geojson',
        data: {
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'LineString',
            coordinates: coordinates
          }
        }
      });
      
      this.map.addLayer({
        id: segmentId,
        type: 'line',
        source: segmentId,
        layout: {
          'line-join': 'round',
          'line-cap': 'round'
        },
        paint: {
          'line-color': '#888',
          'line-width': 2,
          'line-dasharray': [2, 2],
          'line-opacity': 0.7 // Static opacity for mobile
        }
      });
      
      this.lineSegments.push(segmentId);
    }

    // Fit bounds
    this.onComplete();
  }

  /**
   * Handle animation completion
   */
  onComplete() {
    if (this.hasCompleted) return; // idempotent
    this.hasCompleted = true;
    this.isPlaying = false;

    // Update play/pause button states
    const playBtn = document.getElementById('play-btn');
    const pauseBtn = document.getElementById('pause-btn');
    if (playBtn) {
      playBtn.style.display = 'flex';
    }
    if (pauseBtn) {
      pauseBtn.style.display = 'none';
    }

    // Show all markers with full opacity (no fading or pulsing)
    this.markers.forEach(marker => {
      if (marker) {
        const el = marker.getElement();
        // Remove all animation and opacity classes
        el.classList.remove('active', 'passed-1', 'passed-2', 'passed-3', 'passed-4', 'passed-far');
        el.style.display = 'flex';
        // Ensure full opacity for end state overview
        el.style.opacity = '1';
        // Remove any CSS animations
        el.style.animation = 'none';
      }
    });

    // Update all line segments to be solid black for overview
    this.updateAllLinesToSolid();

    // Enhanced auto zoom-out to show complete biography overview
    this.zoomToCompleteOverview();

    // Dispatch completion event
    window.dispatchEvent(new CustomEvent('biographyAnimationComplete'));
  }

  /**
   * Update all line segments to solid black for end state overview
   */
  updateAllLinesToSolid() {
    // First, make sure all line segments exist
    for (let i = 1; i < this.fragments.length; i++) {
      const segmentId = `journey-line-${i-1}-${i}`;
      if (!this.lineSegments.includes(segmentId) && this.fragments[i-1] && this.fragments[i]) {
        // Add missing line segment
        const coordinates = [
          [this.fragments[i-1].lng, this.fragments[i-1].lat],
          [this.fragments[i].lng, this.fragments[i].lat]
        ];

        this.map.addSource(segmentId, {
          type: 'geojson',
          data: {
            type: 'Feature',
            properties: {},
            geometry: {
              type: 'LineString',
              coordinates: coordinates
            }
          }
        });

        this.map.addLayer({
          id: segmentId,
          type: 'line',
          source: segmentId,
          layout: {
            'line-join': 'round',
            'line-cap': 'round'
          },
          paint: {
            'line-color': '#000000',
            'line-width': 2,
            'line-dasharray': [1, 0], // Solid line
            'line-opacity': 1.0
          }
        });

        this.lineSegments.push(segmentId);
      }
    }

    // Now update all line segments to be solid black and fully visible
    this.lineSegments.forEach((segmentId) => {
      if (this.map.getLayer(segmentId)) {
        this.map.setPaintProperty(segmentId, 'line-color', '#000000');
        this.map.setPaintProperty(segmentId, 'line-dasharray', [1, 0]); // Solid line
        this.map.setPaintProperty(segmentId, 'line-opacity', 1.0);
        this.map.setPaintProperty(segmentId, 'line-width', 2);
      }
    });
  }

  /**
   * Enhanced zoom-out to show complete biography overview
   */
  zoomToCompleteOverview() {
    if (this.fragments.length === 0) return;

    const bounds = new mapboxgl.LngLatBounds();

    // Include all biography fragment locations
    this.fragments.forEach(f => {
      if (f.lng && f.lat && !isNaN(f.lng) && !isNaN(f.lat)) {
        bounds.extend([f.lng, f.lat]);
      }
    });

    // Add some buffer around the bounds for better visual presentation
    const padding = {
      top: 80,
      bottom: 80,
      left: 80,
      right: 80
    };

    // Smooth zoom-out with longer duration for cinematic effect
    this.map.fitBounds(bounds, {
      padding: padding,
      duration: 3000, // Longer duration for dramatic effect
      maxZoom: 10,    // Don't zoom in too much
      essential: true
    });
  }

  /**
   * Zoom in on the map
   */
  zoomIn() {
    const currentZoom = this.map.getZoom();
    this.map.setZoom(Math.min(currentZoom + 1, 18)); // Max zoom level 18
  }

  /**
   * Zoom out on the map
   */
  zoomOut() {
    const currentZoom = this.map.getZoom();
    this.map.setZoom(Math.max(currentZoom - 1, 1)); // Min zoom level 1
  }

  /**
   * Setup zoom controls event listeners
   */
  setupZoomControls() {
    const zoomInBtn = document.getElementById('zoom-in-btn');
    const zoomOutBtn = document.getElementById('zoom-out-btn');

    if (zoomInBtn) {
      zoomInBtn.addEventListener('click', () => this.zoomIn());
    }

    if (zoomOutBtn) {
      zoomOutBtn.addEventListener('click', () => this.zoomOut());
    }

    // Add keyboard shortcuts for zoom
    this.setupZoomKeyboardShortcuts();
  }

  /**
   * Setup keyboard shortcuts for zoom functionality
   */
  setupZoomKeyboardShortcuts() {
    document.addEventListener('keydown', (e) => {
      // Only handle zoom shortcuts when map is visible/focused
      const mapContainer = document.querySelector('.biography-map-container');
      if (!mapContainer || !this.map) return;

      // Check if UIKit lightbox is open - if so, don't handle navigation
      const lightboxOpen = document.querySelector('.uk-lightbox.uk-open');
      if (lightboxOpen) return;

      // Check if input is focused
      const activeElement = document.activeElement;
      const isInputFocused = activeElement && (
        activeElement.tagName === 'INPUT' ||
        activeElement.tagName === 'TEXTAREA' ||
        activeElement.contentEditable === 'true'
      );

      if (isInputFocused) return;

      // Handle zoom shortcuts
      if ((e.ctrlKey || e.metaKey) && e.key === '=') {
        e.preventDefault();
        this.zoomIn();
      } else if ((e.ctrlKey || e.metaKey) && e.key === '-') {
        e.preventDefault();
        this.zoomOut();
      }
    });
  }

  /**
   * Get animation controls HTML (mobile only - desktop controls are in timeline)
   */
  static getControlsHTML() {
    // Desktop controls overlay the map
    const isMobile = window.innerWidth < 768;
    const buttonSize = '24';

    if (isMobile) {
      // Mobile: play/pause + zoom (Lucide icons)
      return `
        <div class="biography-map-controls mobile">
          <button id="play-btn" class="timeline-control-btn" title="Play" aria-label="Play">
            <svg xmlns="http://www.w3.org/2000/svg" width="${buttonSize}" height="${buttonSize}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="5 3 19 12 5 21 5 3"></polygon></svg>
          </button>
          <button id="pause-btn" class="timeline-control-btn" title="Pause" aria-label="Pause" style="display: none;">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-pause-icon lucide-pause"><rect x="14" y="3" width="5" height="18" rx="1"/><rect x="5" y="3" width="5" height="18" rx="1"/></svg>
          </button>
          <button id="zoom-in-btn" class="zoom-btn" title="Zoom In" aria-label="Zoom In">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
          </button>
          <button id="zoom-out-btn" class="zoom-btn" title="Zoom Out" aria-label="Zoom Out">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="5" y1="12" x2="19" y2="12"></line></svg>
          </button>
        </div>
      `;
    } else {
      // Desktop controls overlay the map with all controls in a horizontal row
      return `
        <div class="biography-map-controls desktop">
          <button id="play-btn" class="timeline-control-btn" title="Play" aria-label="Play">
            <svg xmlns="http://www.w3.org/2000/svg" width="${buttonSize}" height="${buttonSize}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="5 3 19 12 5 21 5 3"></polygon></svg>
          </button>
          <button id="pause-btn" class="timeline-control-btn" title="Pause" aria-label="Pause" style="display: none;">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-pause-icon lucide-pause"><rect x="14" y="3" width="5" height="18" rx="1"/><rect x="5" y="3" width="5" height="18" rx="1"/></svg>
          </button>
          <button id="zoom-in-btn" class="zoom-btn" title="Zoom In (Ctrl/Cmd +)" aria-label="Zoom In">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
          </button>
          <button id="zoom-out-btn" class="zoom-btn" title="Zoom Out (Ctrl/Cmd -)" aria-label="Zoom Out">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="5" y1="12" x2="19" y2="12"></line></svg>
          </button>
        </div>
      `;
    }
  }
}

export default BiographyMapAnimation;
